import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import MobileAppLayout from "../../layouts/MobileAppLayout";
import { getMyBadgeProgress, type BadgeProgress } from "../../services/badges";
import { GameCard, GameHero, GameIcon, SegmentedControl, Skeleton } from "../../components/game";
import { formatDate } from "../../lib/dateFormat";

// Rarity tier → ring color + glow. Unknown tiers fall back to bronze.
// Tier labels are resolved via i18n at render time (see tierLabelKeys).
const TIERS: Record<string, { ring: string; glow?: string }> = {
  bronze: { ring: "#c2833b" },
  silver: { ring: "#94a3b8" },
  gold: { ring: "#eab308", glow: "0 0 0 3px rgba(234,179,8,.15)" },
  platinum: { ring: "#22d3ee", glow: "0 0 10px rgba(34,211,238,.5)" },
};
const tierOf = (t: string) => TIERS[t] || TIERS.bronze;
const TIER_FALLBACK: Record<string, string> = {
  bronze: "Bronze",
  silver: "Silver",
  gold: "Gold",
  platinum: "Platinum",
};
const tierLabelFor = (tier: string, t: (k: string, d: string) => string) => {
  const key = tier in TIER_FALLBACK ? tier : "bronze";
  return t(`badges.tier_${key}`, TIER_FALLBACK[key]);
};

// Category grouping (order + fallback label + icon). Anything else lands in "Altele".
// The "points" category renders a drawn star (never money — kids app), so
// its icon string is intentionally empty and handled at render time.
// Labels are resolved via i18n at render time (badges.category_<key>).
const CATEGORIES: { key: string; label: string; icon: string }[] = [
  { key: "quiz", label: "Quiz", icon: "🎓" },
  { key: "attendance", label: "Prezență", icon: "🏃" },
  { key: "video", label: "Video", icon: "🎬" },
  { key: "points", label: "Puncte", icon: "" },
  { key: "competition", label: "Competiție", icon: "🏆" },
  { key: "special", label: "Special", icon: "⭐" },
  { key: "__other", label: "Altele", icon: "🎖️" },
];

function BadgeTile({ b, onClick }: { b: BadgeProgress; onClick?: () => void }) {
  const { t } = useTranslation();
  const tier = tierOf(b.tier);
  const locked = !b.earned;
  const pct = b.progress ? Math.round((b.progress.current / b.progress.target) * 100) : 0;
  return (
    <button
      onClick={onClick}
      className={`relative rounded-2xl border p-3 pt-3.5 text-center transition-transform active:translate-y-[2px] ${
        locked
          ? "bg-slate-50 border-slate-200"
          : "bg-white border-slate-100 shadow-[0_3px_0_#eef2f8]"
      }`}
    >
      {/* corner status */}
      {locked ? (
        <GameIcon name="lock" className="absolute top-2 right-2 w-3.5 h-3.5 text-slate-300" />
      ) : (
        <GameIcon name="check" className="absolute top-2 right-2 w-4 h-4 text-emerald-500" />
      )}

      {/* medal + tier ring */}
      <div className="relative w-14 h-14 mx-auto mb-2">
        <div
          className="absolute -inset-[3px] rounded-full border-[2.5px]"
          style={{
            borderColor: locked ? "#e2e8f0" : tier.ring,
            boxShadow: locked ? undefined : tier.glow,
          }}
        />
        <div
          className="w-14 h-14 rounded-full grid place-items-center text-2xl overflow-hidden"
          style={
            locked
              ? { background: "#f1f5f9", color: "#cbd5e1", filter: "grayscale(1)" }
              : { background: `${b.color}22`, color: b.color }
          }
        >
          {b.icon_image_url ? (
            <img src={b.icon_image_url} alt={b.name} className="w-full h-full object-contain p-1.5" />
          ) : (
            b.icon_emoji || "🏅"
          )}
        </div>
      </div>

      <p className={`text-[11px] font-bold leading-tight ${locked ? "text-slate-400" : "text-slate-800"}`}>
        {b.name}
      </p>

      {locked ? (
        b.progress ? (
          <div className="mt-1.5">
            <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-slate-400 rounded-full" style={{ width: `${pct}%` }} />
            </div>
            <p className="text-[9.5px] text-slate-400 font-bold mt-1 tabular-nums">
              {b.progress.current}/{b.progress.target}
            </p>
          </div>
        ) : (
          <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wide mt-1.5">{t("badges.locked", "Blocată")}</p>
        )
      ) : (
        <p
          className="text-[8.5px] font-extrabold uppercase tracking-wide mt-1"
          style={{ color: tier.ring }}
        >
          {tierLabelFor(b.tier, t)}
        </p>
      )}
    </button>
  );
}

export default function AppBadges() {
  const { t } = useTranslation();
  const [badges, setBadges] = useState<BadgeProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [onlyEarned, setOnlyEarned] = useState(false);
  const [selected, setSelected] = useState<BadgeProgress | null>(null);

  const loadBadges = async () => {
    setLoading(true);
    setError(false);
    try {
      setBadges(await getMyBadgeProgress());
    } catch (err) {
      console.error("Failed to load badges:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBadges();
  }, []);

  const earnedCount = badges.filter((b) => b.earned).length;
  const pct = badges.length ? Math.round((earnedCount / badges.length) * 100) : 0;

  const grouped = useMemo(() => {
    const filtered = onlyEarned ? badges.filter((b) => b.earned) : badges;
    return CATEGORIES.map((cat) => ({
      ...cat,
      items: filtered.filter((b) =>
        cat.key === "__other"
          ? !CATEGORIES.some((c) => c.key !== "__other" && c.key === b.category)
          : b.category === cat.key
      ),
    })).filter((g) => g.items.length > 0);
  }, [badges, onlyEarned]);

  return (
    <MobileAppLayout>
      <div className="pb-24">
        {/* Header */}
        <GameHero className="px-4 pt-6 pb-6 rounded-br-3xl">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-2xl bg-white/15 grid place-items-center">
              <GameIcon name="medal" className="w-6 h-6" />
            </span>
            <h1 className="text-2xl font-extrabold">{t("badges.myBadges", "Insignele mele")}</h1>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <div className="flex-1">
              <div className="h-2.5 bg-white/25 rounded-full overflow-hidden">
                <div className="h-full bg-white rounded-full transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
            <span className="text-sm font-extrabold tabular-nums whitespace-nowrap">
              {earnedCount} / {badges.length}
            </span>
          </div>
        </GameHero>

        {/* Filter */}
        <div className="px-4 pt-4">
          <SegmentedControl
            options={[
              { value: "all", label: t("badges.filterAll", "Toate") },
              { value: "earned", label: t("badges.filterEarned", "Câștigate") },
            ]}
            value={onlyEarned ? "earned" : "all"}
            onChange={(v) => setOnlyEarned(v === "earned")}
          />
        </div>

        {loading ? (
          <div className="px-4 pt-4 space-y-5 animate-fadeIn">
            {[0, 1].map((s) => (
              <GameCard key={s} className="p-3.5">
                <Skeleton className="h-3.5 w-24 rounded-full mb-3" />
                <div className="grid grid-cols-3 gap-2.5">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="rounded-2xl border border-slate-100 bg-white p-3 flex flex-col items-center">
                      <Skeleton className="w-14 h-14 rounded-full mb-2" />
                      <Skeleton className="h-2.5 w-14 rounded-full" />
                      <Skeleton className="h-2 w-8 rounded-full mt-1.5" />
                    </div>
                  ))}
                </div>
              </GameCard>
            ))}
          </div>
        ) : error ? (
          <div className="px-4 pt-4">
            <GameCard className="p-6 text-center">
              <h3 className="text-base font-bold text-slate-800 mb-1">{t("common.loadErrorTitle", "Ceva n-a mers")}</h3>
              <p className="text-sm text-slate-500">
                {t("common.loadError", "Nu am putut încărca datele. Verifică conexiunea și încearcă din nou.")}
              </p>
              <button
                onClick={loadBadges}
                className="mt-3 px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white"
              >
                {t("common.retry", "Reîncearcă")}
              </button>
            </GameCard>
          </div>
        ) : grouped.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            {onlyEarned ? t("badges.noneEarned", "Nicio insignă câștigată încă. Continuă cu quiz-urile!") : t("badges.noneAvailable", "Nicio insignă disponibilă.")}
          </div>
        ) : (
          <div className="px-4 pt-4 space-y-5">
            {grouped.map((g) => (
              <GameCard key={g.key} className="p-3.5">
                <h2 className="text-xs font-extrabold uppercase tracking-wider text-slate-500 mb-2.5 flex items-center gap-1.5">
                  {g.key === "points" ? (
                    <GameIcon name="star" className="w-4 h-4" />
                  ) : (
                    <span>{g.icon}</span>
                  )}
                  {t(`badges.category_${g.key === "__other" ? "other" : g.key}`, g.label)}
                </h2>
                <div className="grid grid-cols-3 gap-2.5">
                  {g.items.map((b) => (
                    <BadgeTile key={b.id} b={b} onClick={() => setSelected(b)} />
                  ))}
                </div>
              </GameCard>
            ))}
          </div>
        )}
      </div>

      {/* Badge detail modal */}
      {selected && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-5 bg-slate-900/60 backdrop-blur-sm animate-fadeIn"
          onClick={() => setSelected(null)}
        >
          <div
            className="w-full max-w-sm bg-white rounded-3xl p-6 text-center shadow-2xl animate-in zoom-in-95"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const b = selected;
              const tier = tierOf(b.tier);
              const locked = !b.earned;
              const pct = b.progress ? Math.round((b.progress.current / b.progress.target) * 100) : 0;
              return (
                <>
                  <div className="relative w-24 h-24 mx-auto mb-4">
                    <div
                      className="absolute -inset-1 rounded-full border-[3px]"
                      style={{ borderColor: locked ? "#e2e8f0" : tier.ring, boxShadow: locked ? undefined : tier.glow }}
                    />
                    <div
                      className="w-24 h-24 rounded-full grid place-items-center text-5xl overflow-hidden"
                      style={
                        locked
                          ? { background: "#f1f5f9", color: "#cbd5e1", filter: "grayscale(1)" }
                          : { background: `${b.color}22`, color: b.color }
                      }
                    >
                      {b.icon_image_url ? (
                        <img src={b.icon_image_url} alt={b.name} className="w-full h-full object-contain p-2" />
                      ) : (
                        b.icon_emoji || "🏅"
                      )}
                    </div>
                    {locked && (
                      <div className="absolute inset-0 grid place-items-center">
                        <GameIcon name="lock" className="w-7 h-7 text-slate-400" />
                      </div>
                    )}
                  </div>

                  <span
                    className="inline-block text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-0.5 rounded-full mb-2"
                    style={{ color: tier.ring, backgroundColor: `${tier.ring}1f` }}
                  >
                    {tierLabelFor(b.tier, t)}
                  </span>
                  <h3 className="text-xl font-extrabold text-slate-800">{b.name}</h3>
                  <p className="text-sm text-slate-500 mt-2 leading-relaxed">{b.description}</p>

                  {b.earned ? (
                    <div className="mt-4 inline-flex items-center gap-1.5 text-emerald-600 font-bold text-sm">
                      <GameIcon name="check" className="w-4 h-4 text-emerald-500" />
                      {b.earned_at
                        ? t("badges.earnedOn", "Câștigată la {{date}}", { date: formatDate(new Date(b.earned_at)) })
                        : t("badges.earned", "Câștigată")}
                    </div>
                  ) : b.progress ? (
                    <div className="mt-4">
                      <div className="h-2.5 bg-slate-200 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: tier.ring }} />
                      </div>
                      <p className="text-xs text-slate-400 font-bold mt-1.5 tabular-nums">
                        {b.progress.current} / {b.progress.target}
                      </p>
                    </div>
                  ) : (
                    <p className="mt-4 text-xs font-bold uppercase tracking-wide text-slate-400">{t("badges.locked", "Blocată")}</p>
                  )}

                  <button
                    onClick={() => setSelected(null)}
                    className="mt-6 w-full py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold transition-colors"
                  >
                    {t("badges.close", "Închide")}
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </MobileAppLayout>
  );
}
